import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { cn } from "@wa/ui";
import { updateLead, type Lead, type LeadStatus } from "../../../lib/clients";
import { Pop, useClosePops } from "../../shell/Pop";
import { StatusPill } from "../../ui";
import { STATUSES, STATUS_TONE, type T } from "./shared";

/**
 * The status cell of a table row, which is also the control that changes it.
 *
 * The board next door moves a lead along the pipeline without opening the
 * record, and the table has to be able to do the same or it is the read-only
 * view of the two: every stage change would send the operator back to the
 * board, which is the screen they had just chosen to leave.
 *
 * Must be rendered inside a `PopHost`. Without one the pill still draws and
 * still takes the click, and nothing opens.
 */
export function LeadStatusMenu({
  lead,
  t,
  onUpdate,
}: {
  lead: Lead;
  t: T;
  onUpdate: (id: string, status: LeadStatus) => void;
}) {
  const close = useClosePops();
  const [busy, setBusy] = useState(false);

  async function pick(next: LeadStatus) {
    close();
    if (next === lead.status) return;
    setBusy(true);
    try {
      await updateLead(lead.id, { status: next });
      onUpdate(lead.id, next);
    } finally {
      setBusy(false);
    }
  }

  return (
    // Above the link overlay the name stretches across the row, or the menu
    // never gets the click and the row opens instead.
    <span className="relative z-[1] flex items-center gap-1.5">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn(
              "rounded-full transition-opacity hover:opacity-80",
              open && "opacity-80",
            )}
            aria-label={t("leads.col.status")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <StatusPill tone={STATUS_TONE[lead.status]}>
              {t(`leads.status.${lead.status}`)}
            </StatusPill>
          </button>
        )}
      >
        {STATUSES.map((s) => (
          <button
            key={s.key}
            type="button"
            className={cn("crm-mi", s.key === lead.status && "is-on")}
            onClick={() => void pick(s.key)}
          >
            <span className="flex-1">{t(`leads.status.${s.key}`)}</span>
            {s.key === lead.status ? <Check className="size-4" /> : null}
          </button>
        ))}
      </Pop>
      {busy ? <Loader2 className="size-3.5 animate-spin text-muted-foreground" /> : null}
    </span>
  );
}
